// pages/AdminUsers.jsx — Admin Board tab: manage KaiLux platform admins
// Lets an existing admin invite teammates (each gets their own login at
// admin.kailux.co.za) and revoke access, instead of everyone sharing one
// login. See backend/main.py's /api/admin/platform-admins/* routes and
// supabase/migrations/004_platform_admins.sql.
import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { listPlatformAdmins, invitePlatformAdmin, revokePlatformAdmin } from '../api/kailux'
import EmptyState from '../components/EmptyState'
import Spinner from '../components/Spinner'


import { T, alpha } from '../theme/theme'
const inp = {
  padding:'10px 12px', background:T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color:T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif", boxSizing:'border-box',
}

function StatusPill({ status }) {
  const map = {
    active:  { bg:T.gnd, fg:T.gn, label:'Active' },
    invited: { bg:T.ord, fg:T.or, label:'Invited — pending' },
  }
  const s = map[status] || { bg:T.s2, fg:T.mt, label:status }
  return (
    <span style={{ background:s.bg, color:s.fg, fontSize:'11px', fontWeight:700,
      padding:'3px 10px', borderRadius:'999px', textTransform:'uppercase', letterSpacing:'0.04em' }}>
      {s.label}
    </span>
  )
}

export default function AdminUsers() {
  const { user } = useAuth()
  const [admins, setAdmins]   = useState([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail]     = useState('')
  const [inviting, setInviting] = useState(false)
  const [msg, setMsg]         = useState('')
  const [err, setErr]         = useState('')

  const load = () => {
    setLoading(true)
    listPlatformAdmins()
      .then(d => setAdmins(d.admins || []))
      .catch(() => setErr('Could not load admin users.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleInvite = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setInviting(true); setErr(''); setMsg('')
    try {
      const res = await invitePlatformAdmin(email.trim())
      setMsg(res.message || 'Invitation sent.')
      setEmail('')
      load()
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not send invitation.')
    } finally {
      setInviting(false)
    }
  }

  const handleRevoke = async (adminId, adminEmail) => {
    if (!window.confirm(`Revoke admin access for ${adminEmail}? They will immediately lose access to the Admin Board.`)) return
    setErr('')
    try {
      await revokePlatformAdmin(adminId)
      load()
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not revoke access.')
    }
  }

  return (
    <div>
      <h2 style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:800, color:T.tx, marginBottom:'4px' }}>
        Admin Users
      </h2>
      <p style={{ fontSize:'13px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>
        People with access to the KaiLux Admin Board at admin.kailux.co.za. Each person logs in with their own account.
      </p>

      <form onSubmit={handleInvite} style={{ display:'flex', gap:'10px', marginBottom:'20px' }}>
        <input
          type="email" required placeholder="teammate@kailux.co.za" value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ ...inp, flex:1, maxWidth:'360px' }}
        />
        <button type="submit" disabled={inviting}
          style={{ padding:'10px 20px', borderRadius:'8px', border:'none',
            background: inviting ? T.mt : T.prg,
            color:T.onpri, fontWeight:700, fontSize:'14px', cursor: inviting ? 'not-allowed' : 'pointer',
            fontFamily:"'Outfit',sans-serif" }}>
          {inviting ? 'Sending…' : 'Invite Admin'}
        </button>
      </form>

      {msg && <div style={{ color:T.gn, fontSize:'13px', marginBottom:'16px' }}>{msg}</div>}
      {err && <div style={{ color:T.rd, fontSize:'13px', marginBottom:'16px' }}>{err}</div>}

      {loading ? (
        <Spinner />
      ) : admins.length === 0 ? (
        <EmptyState message="No admin users yet." />
      ) : (
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
          {admins.map((a, i) => (
            <div key={a.id} style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'14px 18px', borderBottom: i < admins.length - 1 ? `1px solid ${T.bd}` : 'none',
            }}>
              <div>
                <div style={{ fontSize:'14px', color:T.tx, fontWeight:600 }}>{a.email}</div>
                <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                  {a.status === 'active'
                    ? `Accepted ${a.accepted_at ? new Date(a.accepted_at).toLocaleDateString() : ''}`
                    : `Invited by ${a.invited_by || 'unknown'} · ${a.invited_at ? new Date(a.invited_at).toLocaleDateString() : ''}`}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                <StatusPill status={a.status} />
                {a.email !== user?.email && (
                  <button
                    onClick={() => handleRevoke(a.id, a.email)}
                    style={{ background:'transparent', border:'none', color:T.rd, fontSize:'13px',
                      fontWeight:600, cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>
                    Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
